import { useState } from "react";
import type { Database } from "@/types/database.types";
import { AppLayout } from "@/components/layout";
import { ProjectCard } from "@/components/project/ProjectCard";
import { CreateProjectDialog } from "@/components/project";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useProjects } from "@/contexts/ProjectsContext";

type MemberProjectRow = {
  role: string;
  user_id: string;
  username: string;
  full_name?: string | null;
  avatar_url?: string | null;
};

type ProjectDataWithMembers =
  Database["public"]["Functions"]["get_user_projects"]["Returns"][number] & {
    members: MemberProjectRow[];
  };

export function Projects() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchQuery] = useState("");
  const { projects, loading: projectsLoading, error: projectsError } = useProjects();

  const filteredProjects = projects.filter((project) => {
    if (searchQuery.trim().length === 0) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      project.project_title.toLowerCase().includes(q) ||
      (project.closest_task_title && project.closest_task_title.toLowerCase().includes(q))
    );
  });

  return (
    <>
      <AppLayout title="Projects">
        <div className="w-full space-y-4">
          {projectsLoading && (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading projects…</div>
          )}
          {projectsError && (
            <div className="text-sm text-destructive py-8 text-center">
              Failed to load projects: {projectsError.message}
            </div>
          )}
          {!projectsLoading && !projectsError && (
            <>
              {filteredProjects.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  {searchQuery ? "No projects found matching your search." : "No projects yet."}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredProjects.map((project) => {
                    const projectWithMembers: ProjectDataWithMembers = {
                      ...project,
                      members: (project.members as unknown as MemberProjectRow[]) || [],
                    };
                    return <ProjectCard key={project.project_id} project={projectWithMembers} />;
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Floating Action Button for New Project */}
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="fixed bottom-8 right-8 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-40"
          size="icon"
          aria-label="Add new project"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </AppLayout>

      <CreateProjectDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          setCreateDialogOpen(false);
        }}
      />
    </>
  );
}